import { useRef, useEffect, useMemo, useState } from 'react';
import {
  View,
  Pressable,
  TextInput,
  ScrollView,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { widthPercentageToDP as wp, heightPercentageToDP as hp } from 'react-native-responsive-screen';
import { Feather } from '@expo/vector-icons';
import DropDownPicker from 'react-native-dropdown-picker';

import CustomText from '../components/CustomText';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CustomContainer from '../components/CustomContainer';
import LoadingOverlay from '../components/LoadingOverlay';
import { useWebTitle } from '../components/WebTooltip';
import { ReportFilterPanel, ReportList, BulkActionBar } from '../components/admin';
import useAdminReports, { TICKET_VIEW_OPTIONS } from '../hooks/useAdminReports';
import { useResponsiveStyles, useViewedReports } from '../hooks';
import styles from '../style/screens/adminFilterHomeStyles';
import AppStyles from '../AppStyles';
import { useTranslation } from '../i18n';

DropDownPicker.setMode('BADGE');

const AdminFilterHome = (props) => {
  const { t } = useTranslation('admin');

  // Ticket view chips carry translation keys; fall back to the English literal
  // so a consumer that supplies bare options still renders something.
  const viewLabel = (option, short) =>
    short
      ? option.shortLabelKey
        ? t(option.shortLabelKey)
        : option.shortLabel
      : option.labelKey
        ? t(option.labelKey)
        : option.label;

  const { organization, admin, filterOrg, userAdmin, initialView } = props.route.params;

  // Use responsive styles hook for platform detection
  const { isWeb, containerStyle, screenWidth } = useResponsiveStyles();

  // Adjust scale for small web screens
  const scale = isWeb && screenWidth < 800 ? 0.7 : 1;

  const {
    reports,
    reportsLoading,
    searchText,
    setSearchText,
    searchReports,
    resetSearch,
    includePreviousReports,
    toggleIncludePreviousReports,
    displayFilter,
    toggleDisplayFilter,
    HROFilterOpen,
    setHROFilterOpen,
    HROFilterValue,
    setHROFilterValue,
    HROFilter,
    setHROFilter,
    KPIFilterOpen,
    setKPIFilterOpen,
    KPIFilterValue,
    setKPIFilterValue,
    KPIFilter,
    setKPIFilter,
    OrgFilterOpen,
    setOrgFilterOpen,
    OrgFilterValue,
    setOrgFilterValue,
    OrgFilter,
    setOrgFilter,
    loadingOrg,
    dateRangeStart,
    setDateRangeStart,
    dateRangeEnd,
    setDateRangeEnd,
    ticketView,
    setTicketView,
    ticketCounts,
    includeAssigned,
    setIncludeAssigned,
    showOlderTickets,
    setShowOlderTickets,
    isTimeConstrained,
    refresh,
    emailReportAsExcel,
    hasComments,
    isClosed,
    // Pagination (infinite scroll)
    loadingMore,
    hasMore,
    loadMore,
    // Bulk operations
    selectMode,
    toggleSelectMode,
    selectedUuids,
    toggleSelect,
    clearSelection,
    analysts,
    bulkLoading,
    bulkMessage,
    bulkClose,
    bulkAssign,
  } = useAdminReports(organization, filterOrg, initialView);

  // Viewed/unread tracking + "Unread only" filter (default: show all)
  const { isViewed, markViewedLocal } = useViewedReports();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const displayedReports = useMemo(
    () => (unreadOnly && Array.isArray(reports) ? reports.filter((r) => !isViewed(r.uuid)) : reports),
    [reports, unreadOnly, isViewed]
  );

  // Select-all must only cover the reports the admin can actually see,
  // so it operates on the (possibly unread-filtered) displayed list.
  const handleSelectAll = () => {
    clearSelection();
    (displayedReports || []).forEach((r) => toggleSelect(r.uuid));
  };

  // Helper to get count for a view
  const getCountForView = (viewKey) => {
    if (!ticketCounts) return null;
    // For allOpen: use unassignedOpen count unless includeAssigned is checked
    if (viewKey === 'allOpen' && !includeAssigned) {
      return ticketCounts.unassignedOpen ?? null;
    }
    return ticketCounts[viewKey] ?? null;
  };

  const scrollRef = useRef(null);
  const isFocused = useIsFocused();

  // Refresh reports when screen comes back into focus (e.g., when navigating back)
  // Use ref to avoid re-firing on every keystroke.
  // refresh changes identity when searchText changes (via executeSearch → searchReports → refresh),
  // but this effect should only fire when the screen gains focus.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (isFocused) {
      refreshRef.current();
    }
  }, [isFocused]);

  const handleReportPress = (reportData) => {
    markViewedLocal(reportData.uuid);
    props.navigation.navigate('AdminReportDetails', reportData);
  };

  const scrollToTop = () => {
    scrollRef.current?.scrollTo({ y: 0, animated: true });
  };

  // Web-only hover tooltips for icon toolbar buttons (no-op on native)
  const emailExcelTooltip = useWebTitle(t('filterHome.emailExcelTooltip'));
  const selectModeTooltip = useWebTitle(selectMode ? t('filterHome.exitSelectMode') : t('filterHome.selectReports'));
  const refreshTooltip = useWebTitle(t('filterHome.refreshReports'));

  const activeView = TICKET_VIEW_OPTIONS.find((o) => o.key === ticketView);

  return (
    <View style={containerStyle}>
      <Header organization={organization} admin={admin} userAdmin={userAdmin} />
      <View style={{ flex: 1, backgroundColor: AppStyles.colorSet.whiteColor }}>
        {/* Title section - V3 style */}
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: screenWidth < 500 ? 8 : hp(1.5), paddingHorizontal: 16 }}>
          {/* Email CSV button - left side */}
          <Pressable
            ref={emailExcelTooltip}
            style={({ pressed }) => [
              { position: 'absolute', left: 16, padding: 8, borderRadius: 8, flexDirection: 'row', alignItems: 'center', gap: 4 },
              pressed && { opacity: 0.7 },
            ]}
            onPress={() => emailReportAsExcel(displayedReports)}
          >
            <Feather name="mail" size={20 * scale} color={AppStyles.colorSet.textSecondary} />
            {screenWidth >= 500 && (
              <CustomText font="medium" size={12} color={AppStyles.colorSet.textSecondary}>
                {t('filterHome.excel')}
              </CustomText>
            )}
          </Pressable>
          <Pressable onPress={scrollToTop}>
            <CustomText
              font="semiBold"
              size={screenWidth < 500 ? 20 : isWeb ? 28 : 24}
              color={AppStyles.colorSet.textPrimary}
              style={{ textAlign: 'center' }}
            >
              {activeView ? viewLabel(activeView, false) : t('filterHome.reports')}
            </CustomText>
          </Pressable>
          <View style={{ position: 'absolute', right: 16, flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            {/* Select mode toggle */}
            <Pressable
              ref={selectModeTooltip}
              style={({ pressed }) => [
                { padding: 8, borderRadius: 8 },
                selectMode && { backgroundColor: `${AppStyles.colorSet.brandPrimary}25` },
                pressed && { opacity: 0.7 },
              ]}
              onPress={toggleSelectMode}
            >
              <Feather
                name="check-square"
                size={22 * scale}
                color={selectMode ? '#B45309' : AppStyles.colorSet.textSecondary}
              />
            </Pressable>
            {/* Refresh */}
            <Pressable
              ref={refreshTooltip}
              style={({ pressed }) => [
                { padding: 8, borderRadius: 8 },
                (pressed || reportsLoading) && { opacity: 0.5 },
              ]}
              onPress={refresh}
              disabled={reportsLoading}
            >
              <Feather name="refresh-cw" size={22 * scale} color={AppStyles.colorSet.textSecondary} />
            </Pressable>
          </View>
        </View>

        {/* Organization indicator */}
        {OrgFilter.length > 1 && (
          <View style={styles.orgIndicatorContainer}>
            <Feather name="briefcase" size={14} color="#4B5563" />
            <CustomText size={13} color={AppStyles.colorSet.textPrimary}>
              {OrgFilterValue.length === 1
                ? OrgFilterValue[0]
                : OrgFilterValue.length === OrgFilter.length
                  ? t('filterHome.allOrganizations')
                  : `${OrgFilterValue.length} organizations selected`}
            </CustomText>
            <Pressable
              onPress={toggleDisplayFilter}
              style={({ pressed }) => [
                styles.orgIndicatorChangeButton,
                pressed && { opacity: 0.7 },
              ]}
            >
              <CustomText size={12} font="medium" color="#B45309">
                {t('filterHome.change')}
              </CustomText>
            </Pressable>
          </View>
        )}

        <CustomContainer
          ref={scrollRef}
          scroll={!isWeb}
          scrollEnabled={!isWeb}
          style={{ maxHeight: '100vh', flex: 1, backgroundColor: AppStyles.colorSet.whiteColor }}
        >
          {/* Ticket view chips — horizontal scroll on small screens */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={{ flexGrow: 0 }}
            contentContainerStyle={[styles.chip_container, { flexGrow: 1, justifyContent: 'center' }]}
          >
            {TICKET_VIEW_OPTIONS.map((option) => {
              const count = getCountForView(option.key);
              const isSelected = ticketView === option.key;
              return (
                <Pressable
                  key={option.key}
                  onPress={() => setTicketView(option.key)}
                  style={isWeb ? { cursor: 'pointer' } : undefined}
                >
                  {({ hovered, focused }) => (
                  <View
                    style={[
                      isSelected ? styles.TicketChipSelected : styles.TicketChip,
                      !isSelected && (hovered || focused) && styles.TicketChipHovered,
                    ]}
                  >
                    <CustomText
                      style={styles.ChipText}
                      font="medium"
                      size={13}
                      color={isSelected ? '#000' : AppStyles.colorSet.textSecondary}
                    >
                      {viewLabel(option, screenWidth < 600)}
                      {count !== null && (
                        <CustomText
                          font="medium"
                          size={12}
                          color={isSelected ? '#666' : AppStyles.colorSet.textMuted}
                        >
                          {` (${count})`}
                        </CustomText>
                      )}
                    </CustomText>
                  </View>
                  )}
                </Pressable>
              );
            })}
          </ScrollView>

          {/* View toggles */}
          <View style={styles.toggleRow}>
              {/* Unread only toggle - all views */}
              <Pressable
                style={({ hovered, focused }) => [
                  styles.toggleButton,
                  unreadOnly && styles.toggleButtonActive,
                  (hovered || focused) && styles.toggleButtonHovered,
                  isWeb && { cursor: 'pointer' },
                ]}
                onPress={() => setUnreadOnly(!unreadOnly)}
                accessibilityRole="switch"
                accessibilityState={{ checked: unreadOnly }}
                accessibilityLabel={t('filterHome.unreadOnlyLabel')}
              >
                <Feather
                  name={unreadOnly ? 'check-square' : 'square'}
                  size={16}
                  color={unreadOnly ? '#B45309' : AppStyles.colorSet.textSecondary}
                />
                <CustomText
                  font="medium"
                  size={12}
                  color={unreadOnly ? '#B45309' : AppStyles.colorSet.textSecondary}
                  style={{ marginLeft: 6 }}
                >
                  {t('filterHome.unreadOnly')}
                </CustomText>
              </Pressable>

              {/* Include assigned toggle - only for All Open view */}
              {ticketView === 'allOpen' && (
                <Pressable
                  style={({ hovered, focused }) => [
                    styles.toggleButton,
                    includeAssigned && styles.toggleButtonActive,
                    (hovered || focused) && styles.toggleButtonHovered,
                    isWeb && { cursor: 'pointer' },
                  ]}
                  onPress={() => setIncludeAssigned(!includeAssigned)}
                >
                  <Feather
                    name={includeAssigned ? 'check-square' : 'square'}
                    size={16}
                    color={includeAssigned ? '#B45309' : AppStyles.colorSet.textSecondary}
                  />
                  <CustomText
                    font="medium"
                    size={12}
                    color={includeAssigned ? '#B45309' : AppStyles.colorSet.textSecondary}
                    style={{ marginLeft: 6 }}
                  >
                    {t('filterHome.includeAssigned')}
                  </CustomText>
                </Pressable>
              )}

              {/* Show older toggle - for time-constrained views */}
              {isTimeConstrained && (
                <Pressable
                  style={({ hovered, focused }) => [
                    styles.toggleButton,
                    showOlderTickets && styles.toggleButtonActive,
                    (hovered || focused) && styles.toggleButtonHovered,
                    isWeb && { cursor: 'pointer' },
                  ]}
                  onPress={() => setShowOlderTickets(!showOlderTickets)}
                >
                  <Feather
                    name={showOlderTickets ? 'check-square' : 'square'}
                    size={16}
                    color={showOlderTickets ? '#B45309' : AppStyles.colorSet.textSecondary}
                  />
                  <CustomText
                    font="medium"
                    size={12}
                    color={showOlderTickets ? '#B45309' : AppStyles.colorSet.textSecondary}
                    style={{ marginLeft: 6 }}
                  >
                    {t('filterHome.showOlder')}
                  </CustomText>
                </Pressable>
              )}
          </View>

          {/* Search box */}
          <View style={{ backgroundColor: AppStyles.colorSet.whiteColor, alignItems: 'center', paddingHorizontal: wp(0.5) }}>
            <View style={styles.textboxContainer}>
              <Pressable
                style={({ hovered, focused }) => [
                  styles.textboxIconContainer,
                  displayFilter
                    ? { borderTopRightRadius: 0, borderBottomRightRadius: 0, borderBottomLeftRadius: 0 }
                    : { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
                  (hovered || focused) && styles.textboxIconHovered,
                  isWeb && { cursor: 'pointer' },
                ]}
                onPress={toggleDisplayFilter}
              >
                <Feather name="filter" size={24 * scale} color="#000" />
              </Pressable>
              <TextInput
                value={searchText}
                onChangeText={setSearchText}
                onSubmitEditing={searchReports}
                placeholder={t('filterHome.searchPlaceholder')}
                placeholderTextColor={AppStyles.colorSet.textMuted}
                style={styles.textboxText}
              />
              <Pressable
                style={({ hovered, focused }) => [
                  styles.textboxResetSearch,
                  (hovered || focused) && styles.textboxResetHovered,
                  isWeb && { cursor: 'pointer' },
                ]}
                onPress={resetSearch}
              >
                <Feather name="x" size={24 * scale} color={AppStyles.colorSet.textMuted} />
              </Pressable>
              <Pressable
                style={({ hovered, focused }) => [
                  styles.textboxIconContainer,
                  displayFilter
                    ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }
                    : { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 },
                  (hovered || focused) && styles.textboxIconHovered,
                  isWeb && { cursor: 'pointer' },
                ]}
                onPress={searchReports}
              >
                <Feather name="search" size={24 * scale} color="#000" />
              </Pressable>
            </View>
          </View>

        {/* Filter panel */}
        {displayFilter && (
          <ReportFilterPanel
            includePreviousReports={includePreviousReports}
            onToggleIncludePreviousReports={toggleIncludePreviousReports}
            HROFilterOpen={HROFilterOpen}
            setHROFilterOpen={setHROFilterOpen}
            HROFilterValue={HROFilterValue}
            setHROFilterValue={setHROFilterValue}
            HROFilter={HROFilter}
            setHROFilter={setHROFilter}
            KPIFilterOpen={KPIFilterOpen}
            setKPIFilterOpen={setKPIFilterOpen}
            KPIFilterValue={KPIFilterValue}
            setKPIFilterValue={setKPIFilterValue}
            KPIFilter={KPIFilter}
            setKPIFilter={setKPIFilter}
            OrgFilter={OrgFilter}
            OrgFilterOpen={OrgFilterOpen}
            setOrgFilterOpen={setOrgFilterOpen}
            OrgFilterValue={OrgFilterValue}
            setOrgFilterValue={setOrgFilterValue}
            setOrgFilter={setOrgFilter}
            loadingOrg={loadingOrg}
            dateRangeStart={dateRangeStart}
            setDateRangeStart={setDateRangeStart}
            dateRangeEnd={dateRangeEnd}
            setDateRangeEnd={setDateRangeEnd}
          />
        )}

          {/* Report list */}
          <View style={[styles.orangeContainer, { flex: 1 }]}>
            <ReportList
              reports={displayedReports}
              reportsLoading={reportsLoading}
              isViewed={isViewed}
              defaultOrganization={organization}
              userAdmin={userAdmin}
              hasComments={hasComments}
              isClosed={isClosed}
              onReportPress={handleReportPress}
              showOrganization={OrgFilterValue.length > 1}
              onLoadMore={loadMore}
              loadingMore={loadingMore}
              hasMore={hasMore}
              selectMode={selectMode}
              selectedUuids={selectedUuids}
              onToggleSelect={toggleSelect}
            />
            <LoadingOverlay visible={!!bulkMessage} overlay message={bulkMessage} />
          </View>
        </CustomContainer>

        {/* Bulk action bar */}
        {selectMode && selectedUuids.length > 0 && (
          <BulkActionBar
            selectedCount={selectedUuids.length}
            totalCount={displayedReports.length}
            onSelectAll={handleSelectAll}
            onClearSelection={clearSelection}
            onClose={bulkClose}
            onAssign={bulkAssign}
            analysts={analysts}
            loading={bulkLoading}
          />
        )}
      </View>

      <Footer admin={admin} organization={organization} userAdmin={userAdmin} />
    </View>
  );
};

export default AdminFilterHome;
